import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Printer, Pencil, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { SearchBar, matchesSearch, usePagination, PaginationNav, PAGE_SIZE } from "@/components/tableUtils";

const EMPTY = { kode: "", nama: "", kategori: "", ruangan: "", merk: "", spesifikasi: "", tanggal_pengadaan: "", status: "Baik" };

export function StatusBadge({ status }) {
  const styles = {
    Baik: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Perbaikan: "bg-amber-100 text-amber-800 border-amber-200",
    Rusak: "bg-red-100 text-red-800 border-red-200",
  };
  return (
    <Badge variant="outline" className={styles[status] || ""} data-testid={`status-badge-${status?.toLowerCase()}`}>
      {status}
    </Badge>
  );
}

export default function Aset() {
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [printAssets, setPrintAssets] = useState([]);
  const [search, setSearch] = useState("");
  const filtered = assets.filter((a) => matchesSearch(a, ["kode", "nama", "ruangan", "kategori", "merk"], search));
  const { page, setPage, pageCount } = usePagination(filtered.length);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const printableCount = assets.filter((a) => selected.has(a.id) && a.is_active).length;

  const load = () => api.get("/assets").then((r) => setAssets(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(filtered.map((a) => a.id)) : new Set());
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      kode: a.kode, nama: a.nama, kategori: a.kategori, ruangan: a.ruangan, merk: a.merk,
      spesifikasi: a.spesifikasi, tanggal_pengadaan: a.tanggal_pengadaan || "", status: a.status,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, tanggal_pengadaan: form.tanggal_pengadaan || null };
      if (editing) await api.put(`/assets/${editing.id}`, payload);
      else await api.post("/assets", payload);
      toast.success(editing ? "Aset diperbarui" : "Aset ditambahkan");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setActive = async (a, active) => {
    try {
      await api.patch(`/assets/${a.id}/active`, { is_active: active });
      toast.success(active ? "Aset diaktifkan" : "Aset dinonaktifkan");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const printQR = () => {
    const list = assets.filter((a) => selected.has(a.id) && a.is_active);
    if (list.length === 0) {
      toast.error("Pilih minimal satu aset aktif untuk dicetak");
      return;
    }
    setPrintAssets(list);
    setTimeout(() => window.print(), 200);
  };

  return (
    <div data-testid="aset-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Data Aset IT</h1>
          <p className="text-sm text-muted-foreground">Kelola seluruh peralatan IT rumah sakit.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={printQR} disabled={printableCount === 0} data-testid="cetak-qr-button">
            <Printer className="w-4 h-4 mr-2" /> Cetak QR ({printableCount})
          </Button>
          <Button onClick={openAdd} data-testid="tambah-aset-button">
            <Plus className="w-4 h-4 mr-2" /> Tambah Aset
          </Button>
        </div>
      </div>

      <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} testid="aset-search" />

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  data-testid="select-all-checkbox"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onCheckedChange={toggleAll}
                />
              </TableHead>
              <TableHead>Kode</TableHead>
              <TableHead>Nama Aset</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead>Ruangan</TableHead>
              <TableHead>Merk</TableHead>
              <TableHead>Spesifikasi</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Aktif</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((a) => (
              <TableRow key={a.id} data-testid={`asset-row-${a.kode}`}>
                <TableCell>
                  <Checkbox
                    data-testid={`select-asset-${a.kode}`}
                    checked={selected.has(a.id)}
                    onCheckedChange={() => toggle(a.id)}
                    disabled={!a.is_active}
                  />
                </TableCell>
                <TableCell className="font-medium">{a.kode}</TableCell>
                <TableCell>{a.nama}</TableCell>
                <TableCell>{a.kategori}</TableCell>
                <TableCell>{a.ruangan}</TableCell>
                <TableCell>{a.merk}</TableCell>
                <TableCell className="max-w-[220px] truncate" title={a.spesifikasi}>{a.spesifikasi || "—"}</TableCell>
                <TableCell><StatusBadge status={a.status} /></TableCell>
                <TableCell>
                  <Badge variant={a.is_active ? "secondary" : "outline"} data-testid={`active-badge-${a.kode}`}>
                    {a.is_active ? "Aktif" : "Nonaktif"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.kode}`} title="Edit">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setActive(a, !a.is_active)}
                      data-testid={`toggle-asset-${a.kode}`}
                      title={a.is_active ? "Nonaktifkan" : "Aktifkan"}
                    >
                      {a.is_active ? <PowerOff className="w-4 h-4 text-destructive" /> : <Power className="w-4 h-4 text-primary" />}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} className="text-center text-muted-foreground py-10" data-testid="assets-empty">
                  {assets.length === 0 ? 'Belum ada aset. Klik "Tambah Aset" untuk memulai.' : "Tidak ada hasil yang cocok dengan pencarian."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <PaginationNav page={page} pageCount={pageCount} onChange={setPage} testid="aset-pagination" />
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="asset-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Aset" : "Tambah Aset"}</DialogTitle>
            <DialogDescription>Lengkapi data aset peralatan IT di bawah ini.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Kode Aset</Label>
              <Input value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} data-testid="asset-form-kode" placeholder="AST-009" />
            </div>
            <div className="space-y-2">
              <Label>Nama Aset</Label>
              <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="asset-form-nama" />
            </div>
            <div className="space-y-2">
              <Label>Kategori</Label>
              <Input value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value })} data-testid="asset-form-kategori" placeholder="Komputer / Printer / ..." />
            </div>
            <div className="space-y-2">
              <Label>Ruangan</Label>
              <Input value={form.ruangan} onChange={(e) => setForm({ ...form, ruangan: e.target.value })} data-testid="asset-form-ruangan" placeholder="IGD / ICU / ..." />
            </div>
            <div className="space-y-2">
              <Label>Merk</Label>
              <Input value={form.merk} onChange={(e) => setForm({ ...form, merk: e.target.value })} data-testid="asset-form-merk" />
            </div>
            <div className="space-y-2">
              <Label>Tanggal Pengadaan</Label>
              <Input type="date" value={form.tanggal_pengadaan} onChange={(e) => setForm({ ...form, tanggal_pengadaan: e.target.value })} data-testid="asset-form-tanggal" />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="asset-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Baik">Baik</SelectItem>
                  <SelectItem value="Perbaikan">Perbaikan</SelectItem>
                  <SelectItem value="Rusak">Rusak</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label>Spesifikasi</Label>
              <Textarea value={form.spesifikasi} onChange={(e) => setForm({ ...form, spesifikasi: e.target.value })} data-testid="asset-form-spesifikasi" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="asset-form-cancel">Batal</Button>
            <Button onClick={save} data-testid="asset-form-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="print-area hidden print:block" data-testid="qr-print-area">
        <h1 className="font-heading font-bold text-xl">SIPAT — Label QR Aset IT</h1>
        <div className="grid grid-cols-3 gap-6 mt-6">
          {printAssets.map((a) => (
            <div key={a.id} className="border border-gray-400 rounded-md p-4 flex flex-col items-center">
              <QRCodeSVG value={`${window.location.origin}/scan/${a.id}`} size={140} />
              <p className="mt-3 text-sm font-bold">{a.kode}</p>
              <p className="text-xs text-center">{a.nama}</p>
              <p className="text-xs text-gray-600">{a.ruangan}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
